// ============================================
// R14 (t46): CREATE INVITE-LINK DIALOG
// ============================================
//
// Opens from the project-settings "Invite links" section.
// Lets the owner/admin pick a role (MEMBER or VIEWER) and an
// optional expiry date. The expiry is "forever" by default —
// the user explicitly checks "expires" to opt in.
//
// Submit returns the created link so the parent can show the
// freshly-minted URL in a copy-confirm toast without an extra
// GET round-trip.

import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import type { ProjectRole } from '@/lib/schemas';
import { useCreateInviteLink, type ProjectInviteLink } from '@/hooks/use-invite-links';

interface InviteLinkDialogProps {
    projectId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCreated?: (link: ProjectInviteLink) => void;
}

export function InviteLinkDialog({
    projectId,
    open,
    onOpenChange,
    onCreated,
}: InviteLinkDialogProps) {
    const { t } = useI18n();
    const create = useCreateInviteLink(projectId);

    const [role, setRole] = useState<ProjectRole>('MEMBER');
    const [hasExpiry, setHasExpiry] = useState(false);
    const [expiry, setExpiry] = useState('');

    const reset = () => {
        setRole('MEMBER');
        setHasExpiry(false);
        setExpiry('');
    };

    const handleSubmit = async () => {
        try {
            const link = await create.mutateAsync({
                role,
                expiresAt:
                    hasExpiry && expiry
                        ? new Date(expiry).toISOString()
                        : null,
            });
            onCreated?.(link);
            reset();
            onOpenChange(false);
        } catch {
            // Error toast handled by api helper / future mutation
            // hook. We don't surface inline errors to avoid double
            // notification.
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('inviteLink.createTitle')}</DialogTitle>
                    <DialogDescription>
                        {t('inviteLink.createDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            {t('inviteLink.role')}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            {(['MEMBER', 'VIEWER'] as ProjectRole[]).map((r) => {
                                const active = role === r;
                                const labelKey =
                                    r === 'MEMBER'
                                        ? 'inviteLink.role.MEMBER'
                                        : 'inviteLink.role.VIEWER';
                                const descKey =
                                    r === 'MEMBER'
                                        ? 'inviteLink.role.MEMBER.description'
                                        : 'inviteLink.role.VIEWER.description';
                                return (
                                    <button
                                        key={r}
                                        type="button"
                                        onClick={() => setRole(r)}
                                        className={
                                            'flex flex-col items-start gap-0.5 rounded-md border p-3 text-left transition ' +
                                            (active
                                                ? 'border-primary bg-primary/10 ring-1 ring-primary'
                                                : 'border-border hover:border-foreground-muted')
                                        }
                                    >
                                        <span className="text-sm font-medium">
                                            {t(labelKey)}
                                        </span>
                                        <span className="text-xs text-foreground-muted">
                                            {t(descKey)}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                            <Checkbox
                                checked={hasExpiry}
                                onCheckedChange={(v) => setHasExpiry(Boolean(v))}
                            />
                            {t('inviteLink.hasExpiry')}
                        </label>
                        {hasExpiry && (
                            <Input
                                type="datetime-local"
                                value={expiry}
                                onChange={(e) => setExpiry(e.target.value)}
                                className="text-sm"
                            />
                        )}
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={create.isPending}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={create.isPending || (hasExpiry && !expiry)}
                    >
                        {create.isPending && (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        )}
                        {t('inviteLink.create')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
